import type { ToolCallEntry } from '@/lib/api/generated/model';
import type { TranscriptEntry } from './transcript';

/**
 * What one editing tool call asks for (spec 157), read from its input.
 *
 * It is a request, not a record: the transcript says what the agent asked the
 * tool to change, not whether that landed or what the file holds now. A call
 * carries no id linking it to its result, so a failed edit still reads as one —
 * the tool error that follows it is what says it failed.
 */
export interface RequestedEdit {
  tool: EditingTool;
  /** As the agent wrote it, e.g. `/workspace/src/a.ts`. */
  path: string;
  /** `path` without the workspace mount, or `null` when it is outside it. */
  workspacePath: string | null;
  /** One per Edit, one per MultiEdit entry, exactly one for Write and NotebookEdit. */
  changes: RequestedChange[];
  /** What the copy control copies: the new text (FR-008). */
  copyText: string;
}

export type EditingTool = 'Edit' | 'MultiEdit' | 'Write' | 'NotebookEdit';

export type RequestedChange =
  | { kind: 'replace'; oldText: string; newText: string; replaceAll: boolean }
  | { kind: 'write'; content: string }
  | { kind: 'cell'; cellId: string | null; mode: CellMode; source: string };

export type CellMode = 'replace' | 'insert' | 'delete';

const CELL_MODES: readonly CellMode[] = ['replace', 'insert', 'delete'];

/** Where a run's sandbox mounts the workspace, as `markdownFilePaths.ts` strips it. */
const WORKSPACE_MOUNT = '/workspace/';

/**
 * The change an Edit, MultiEdit, Write or NotebookEdit call asks for, or `null`
 * for any other tool and for input that is not the shape these tools take.
 * `null` means "render it as today's JSON" (FR-009): a field missing or of the
 * wrong kind never hides what the call contained, and one bad MultiEdit entry
 * rejects the whole call rather than showing the rest as if it were all.
 */
export function readRequestedEdit(entry: ToolCallEntry): RequestedEdit | null {
  const input = entry.input;
  switch (entry.tool_name) {
    case 'Edit': {
      const path = nonEmptyString(input.file_path);
      const change = readReplacement(input);
      if (path === null || change === null) return null;
      return edit('Edit', path, [change], change.newText);
    }

    case 'MultiEdit': {
      const path = nonEmptyString(input.file_path);
      if (path === null || !Array.isArray(input.edits)) return null;
      if (input.edits.length === 0) return null;
      const changes: Extract<RequestedChange, { kind: 'replace' }>[] = [];
      for (const raw of input.edits) {
        const change = isRecord(raw) ? readReplacement(raw) : null;
        if (change === null) return null;
        changes.push(change);
      }
      return edit(
        'MultiEdit',
        path,
        changes,
        changes.map((change) => change.newText).join('\n\n'),
      );
    }

    case 'Write': {
      const path = nonEmptyString(input.file_path);
      if (path === null || typeof input.content !== 'string') return null;
      return edit(
        'Write',
        path,
        [{ kind: 'write', content: input.content }],
        input.content,
      );
    }

    case 'NotebookEdit': {
      const path = nonEmptyString(input.notebook_path);
      const mode = input.edit_mode ?? 'replace';
      const cellId = input.cell_id ?? null;
      if (path === null || !isCellMode(mode)) return null;
      if (cellId !== null && typeof cellId !== 'string') return null;
      // A deleted cell needs no source; every other mode is its source.
      const source =
        mode === 'delete' && input.new_source === undefined
          ? ''
          : input.new_source;
      if (typeof source !== 'string') return null;
      return edit(
        'NotebookEdit',
        path,
        [{ kind: 'cell', cellId, mode, source }],
        source,
      );
    }

    default:
      return null;
  }
}

/** The path inside the workspace, or `null` for one outside it (`/workdir/…`, relative). */
export function workspacePathOf(path: string): string | null {
  if (!path.startsWith(WORKSPACE_MOUNT)) return null;
  const inside = path.slice(WORKSPACE_MOUNT.length);
  return inside === '' ? null : inside;
}

/**
 * Every workspace file a transcript's editing calls name, each once, in the
 * order they first appear — what the run's existence check asks about.
 */
export function editedWorkspacePaths(
  entries: readonly TranscriptEntry[],
): string[] {
  const paths = new Set<string>();
  for (const entry of entries) {
    if (entry.type !== 'tool_call') continue;
    const workspacePath = readRequestedEdit(entry)?.workspacePath;
    if (workspacePath) paths.add(workspacePath);
  }
  return [...paths];
}

function edit(
  tool: EditingTool,
  path: string,
  changes: RequestedChange[],
  copyText: string,
): RequestedEdit {
  return {
    tool,
    path,
    workspacePath: workspacePathOf(path),
    changes,
    copyText,
  };
}

function readReplacement(
  input: Record<string, unknown>,
): Extract<RequestedChange, { kind: 'replace' }> | null {
  const { old_string: oldText, new_string: newText } = input;
  const replaceAll = input.replace_all ?? false;
  if (typeof oldText !== 'string' || typeof newText !== 'string') return null;
  if (typeof replaceAll !== 'boolean') return null;
  return { kind: 'replace', oldText, newText, replaceAll };
}

function nonEmptyString(value: unknown): string | null {
  return typeof value === 'string' && value !== '' ? value : null;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isCellMode(value: unknown): value is CellMode {
  return CELL_MODES.includes(value as CellMode);
}
